<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("member_level.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin"
                        size="md"
                        type="neutral"
                        @click="openCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("member_level.btn_add") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("member_level.title") }}</h3>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="sortedList"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('member_level.no_data')"
                    >
                        <el-table-column :label="$t('member_level.col_code')" min-width="90px">
                            <template slot-scope="{ row }">
                                <small>{{ row.code }}</small>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_name')" min-width="160px">
                            <template slot-scope="{ row }">
                                {{ row.name_th }} / {{ row.name_en }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_min')" min-width="160px">
                            <template slot-scope="{ row }">
                                {{ money(row.min_spending) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_rate')" min-width="140px">
                            <template slot-scope="{ row }">
                                {{ row.points_per_baht_x100 }} {{ $t("member_level.pts_per_100") }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_members')" min-width="100px">
                            <template slot-scope="{ row }">
                                {{ row.member_count }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_free_shipping')" min-width="120px" align="center">
                            <template slot-scope="{ row }">
                                <i
                                    v-if="row.free_shipping"
                                    class="fa fa-check text-success"
                                ></i>
                                <span v-else class="text-muted">−</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_discount')" min-width="110px" align="right">
                            <template slot-scope="{ row }">
                                {{ percent(row.discount_percent) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_coin')" min-width="110px" align="right">
                            <template slot-scope="{ row }">
                                <span v-if="Number(row.cashback_percent) > 0">
                                    {{ percent(row.cashback_percent) }}
                                </span>
                                <!-- 0 is not "0%": §1.2 reads it as "not set for
                                     this tier", which falls through to the store
                                     default, so a plain 0% would be a lie. -->
                                <span v-else class="text-muted" :title="$t('member_level.hint_coin_unset')">−</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_privileges')" min-width="120px" align="center">
                            <template slot-scope="{ row }">
                                <span
                                    v-if="privilegeCount(row) > 0"
                                    class="badge badge-pill badge-primary"
                                    :title="$t('member_level.privilege_count_hint', {
                                        active: activePrivilegeCount(row),
                                        total: privilegeCount(row)
                                    })"
                                >{{ activePrivilegeCount(row) }} / {{ privilegeCount(row) }}</span>
                                <span v-else class="text-muted">−</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('member_level.col_color')" min-width="120px">
                            <template slot-scope="{ row }">
                                <span
                                    :style="{
                                        display: 'inline-block',
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: row.color,
                                        borderRadius: '4px',
                                        verticalAlign: 'middle',
                                        marginRight: '6px'
                                    }"
                                ></span>
                                <small>{{ row.color }}</small>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="120px" align="right">
                            <template slot-scope="{ row }">
                                <base-button
                                    v-if="isAdmin"
                                    size="sm"
                                    type="warning"
                                    class="mr-1"
                                    @click="openEdit(row)"
                                >
                                    <i class="fa fa-edit"></i>
                                </base-button>
                                <base-button
                                    v-if="isAdmin && Number(row.min_spending) !== 0"
                                    size="sm"
                                    type="danger"
                                    @click="confirmDelete(row)"
                                >
                                    <i class="fa fa-trash"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>
        </div>

        <!-- Create / Edit Dialog -->
        <el-dialog
            :title="editMode ? $t('member_level.edit_title') : $t('member_level.btn_add')"
            :visible.sync="dialogVisible"
            width="760px"
            @closed="resetForm"
        >
            <el-form
                ref="levelForm"
                :model="form"
                :rules="rules"
                label-width="160px"
            >
                <el-form-item :label="$t('member_level.label_code')" prop="code">
                    <el-input
                        v-model="form.code"
                        :disabled="editMode"
                        placeholder="e.g. silver"
                    ></el-input>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_name_th')" prop="name_th">
                    <el-input v-model="form.name_th"></el-input>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_name_en')" prop="name_en">
                    <el-input v-model="form.name_en"></el-input>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_min')" prop="min_spending">
                    <el-input-number
                        v-model="form.min_spending"
                        :min="0"
                        :disabled="editMode && originalMin === 0"
                    ></el-input-number>
                    <div v-if="editMode && originalMin === 0" class="text-muted" style="font-size:12px;margin-top:4px;">
                        {{ $t("member_level.hint_base_locked") }}
                    </div>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_rate')" prop="points_per_baht_x100">
                    <el-input-number
                        v-model="form.points_per_baht_x100"
                        :min="1"
                        :step="1"
                        :precision="0"
                    ></el-input-number>
                    <div class="text-muted" style="font-size:12px;margin-top:4px;">
                        {{ $t("member_level.hint_rate") }}
                    </div>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_free_shipping')" prop="free_shipping">
                    <el-switch v-model="form.free_shipping"></el-switch>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_discount')" prop="discount_percent">
                    <el-input-number
                        v-model="form.discount_percent"
                        :min="0"
                        :max="100"
                        :step="1"
                        :precision="2"
                    ></el-input-number>
                    <div class="text-muted" style="font-size:12px;margin-top:4px;">
                        {{ $t("member_level.hint_discount") }}
                    </div>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_coin')" prop="cashback_percent">
                    <el-input-number
                        v-model="form.cashback_percent"
                        :min="0"
                        :max="100"
                        :step="0.5"
                        :precision="2"
                    ></el-input-number>
                    <div class="text-muted" style="font-size:12px;margin-top:4px;">
                        {{ $t("member_level.hint_coin") }}
                    </div>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_color')" prop="color">
                    <el-color-picker v-model="form.color"></el-color-picker>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_icon')" prop="icon">
                    <el-input v-model="form.icon" placeholder="e.g. fa-medal"></el-input>
                </el-form-item>
                <!-- Privilege list (§5): the customer-facing benefit lines,
                     ordered by the ▲/▼ buttons. Array order IS sort_order —
                     normalizePrivileges re-indexes on save. -->
                <el-form-item :label="$t('member_level.label_privileges')">
                    <div v-if="!form.privileges.length" class="text-muted" style="font-size:12px;">
                        {{ $t("member_level.privilege_empty") }}
                    </div>
                    <div
                        v-for="(row, index) in form.privileges"
                        :key="row._key"
                        class="privilege-row"
                    >
                        <div class="privilege-row__move">
                            <base-button
                                size="sm"
                                type="secondary"
                                class="privilege-row__btn"
                                :disabled="index === 0"
                                :title="$t('member_level.privilege_move_up')"
                                @click="movePrivilege(index, -1)"
                            >
                                <i class="fa fa-arrow-up"></i>
                            </base-button>
                            <base-button
                                size="sm"
                                type="secondary"
                                class="privilege-row__btn"
                                :disabled="index === form.privileges.length - 1"
                                :title="$t('member_level.privilege_move_down')"
                                @click="movePrivilege(index, 1)"
                            >
                                <i class="fa fa-arrow-down"></i>
                            </base-button>
                        </div>
                        <el-select
                            v-model="row.icon"
                            size="small"
                            clearable
                            class="privilege-row__icon"
                            :placeholder="$t('member_level.privilege_icon')"
                        >
                            <el-option
                                v-for="icon in privilegeIcons"
                                :key="icon"
                                :value="icon"
                                :label="$t('member_level.privilege_icon_' + icon)"
                            >
                                <i :class="iconClass(icon)" style="margin-right:6px;"></i>
                                <span>{{ $t("member_level.privilege_icon_" + icon) }}</span>
                            </el-option>
                        </el-select>
                        <!-- Shows what the member app will draw, including the
                             neutral placeholder for an icon this build does not
                             know (§5 "unknown icon ⇒ ไอคอนกลาง"). -->
                        <i :class="iconClass(row.icon)" class="privilege-row__preview"></i>
                        <el-input
                            v-model="row.label_th"
                            size="small"
                            class="privilege-row__label"
                            :placeholder="$t('member_level.privilege_label_th')"
                        ></el-input>
                        <el-input
                            v-model="row.label_en"
                            size="small"
                            class="privilege-row__label"
                            :placeholder="$t('member_level.privilege_label_en')"
                        ></el-input>
                        <el-switch
                            v-model="row.is_active"
                            :title="$t('member_level.privilege_active')"
                        ></el-switch>
                        <base-button
                            size="sm"
                            type="danger"
                            class="privilege-row__btn"
                            :title="$t('member_level.privilege_remove')"
                            @click="removePrivilege(index)"
                        >
                            <i class="fa fa-times"></i>
                        </base-button>
                    </div>
                    <base-button
                        size="sm"
                        type="secondary"
                        class="mt-2"
                        @click="addPrivilege"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("member_level.btn_add_privilege") }}
                    </base-button>
                    <div class="text-muted" style="font-size:12px;margin-top:4px;">
                        {{ $t("member_level.hint_privileges") }}
                    </div>
                    <div v-if="privilegeIconWarning" class="text-warning" style="font-size:12px;">
                        <i class="fa fa-exclamation-triangle"></i>
                        {{ $t("member_level.warn_unknown_icon") }}
                    </div>
                    <div v-if="privilegeDiffText" style="font-size:12px;" class="text-info">
                        {{ privilegeDiffText }}
                    </div>
                </el-form-item>
                <el-form-item :label="$t('member_level.label_benefits')" prop="benefits_json">
                    <el-input
                        v-model="form.benefits_json"
                        type="textarea"
                        :rows="3"
                        placeholder='{"free_shipping":true}'
                    ></el-input>
                    <div class="text-muted" style="font-size:12px;margin-top:4px;">
                        {{ $t("member_level.hint_benefits_legacy") }}
                    </div>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="dialogVisible = false">{{ $t("cancel") || "Cancel" }}</base-button>
                <base-button type="primary" :loading="saving" @click="submitForm">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import {
    Table,
    TableColumn,
    Dialog,
    Form,
    FormItem,
    Input,
    InputNumber,
    ColorPicker,
    Select,
    Option,
    Switch
} from "element-ui";
import {
    PRIVILEGE_ICONS,
    ERR_EMPTY_LABEL_TH,
    ERR_UNKNOWN_ICON,
    normalizePrivileges,
    privilegesFromApi,
    privilegesDiff,
    validatePrivileges,
    cashbackPercentError,
    privilegeIconClass
} from "@/utils/memberLevelPrivileges";

// _key gives every editor row an identity that survives a reorder. Without it
// the v-for key would be the index, and moving a row would leave Vue reusing
// the <el-input> of the row that used to sit there — the labels appear to
// "stick" while the icons move. It is stripped on save: normalizePrivileges
// rebuilds each row from the fields §4 accepts and copies nothing else.
let privilegeKeySeq = 0;
function withKeys(rows) {
    return rows.map((row) =>
        Object.assign({}, row, { _key: "p" + ++privilegeKeySeq })
    );
}

// One source of truth for an empty form — the dialog is reset from three places
// (open-create, @closed, and the initial data()), and a field added to only two
// of them is a field that leaks from the last tier edited into the next.
function blankForm() {
    return {
        id: null,
        code: "",
        name_th: "",
        name_en: "",
        min_spending: 0,
        points_per_baht_x100: 1,
        free_shipping: false,
        discount_percent: 0,
        cashback_percent: 0,
        color: "#409EFF",
        icon: "",
        benefits_json: "",
        privileges: []
    };
}

export default {
    name: "MemberLevels",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Dialog.name]: Dialog,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [InputNumber.name]: InputNumber,
        [ColorPicker.name]: ColorPicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Switch.name]: Switch
    },
    data() {
        const validateCode = (rule, value, callback) => {
            if (!value || value === "") {
                return callback(new Error(this.$t("member_level.field_required")));
            }
            if (!/^[a-z0-9_]{1,32}$/.test(value)) {
                return callback(new Error(this.$t("member_level.err_code")));
            }
            return callback();
        };
        const validateMin = (rule, value, callback) => {
            if (value === null || value === undefined || value === "") {
                return callback(new Error(this.$t("member_level.field_required")));
            }
            if (this.isDuplicateMin(value)) {
                return callback(new Error(this.$t("member_level.err_dup_min")));
            }
            return callback();
        };
        const validateRate = (rule, value, callback) => {
            if (value === null || value === undefined || value === "") {
                return callback(new Error(this.$t("member_level.field_required")));
            }
            if (value < 1) {
                return callback(new Error(this.$t("member_level.field_required")));
            }
            return callback();
        };
        const validateBenefits = (rule, value, callback) => {
            if (!value || value.trim() === "") {
                return callback();
            }
            try {
                JSON.parse(value);
                return callback();
            } catch (e) {
                return callback(new Error(this.$t("member_level.err_benefits_json")));
            }
        };
        return {
            dialogVisible: false,
            editMode: false,
            saving: false,
            originalMin: 0,
            // The list as it arrived from the GET, kept so the dialog can say
            // what a save would change. Display only — the PUT always carries
            // the full list (§4).
            originalPrivileges: [],
            form: blankForm(),
            rules: {
                code: [{ required: true, validator: validateCode, trigger: "blur" }],
                name_th: [{ required: true, message: this.$t("member_level.field_required"), trigger: "blur" }],
                name_en: [{ required: true, message: this.$t("member_level.field_required"), trigger: "blur" }],
                min_spending: [{ required: true, validator: validateMin, trigger: "blur" }],
                points_per_baht_x100: [{ required: true, validator: validateRate, trigger: "blur" }],
                benefits_json: [{ validator: validateBenefits, trigger: "blur" }]
            }
        };
    },
    computed: {
        ...mapGetters({
            list: "MemberLevel/list",
            loading: "MemberLevel/loading",
            isAdmin: "Auth/isAdmin"
        }),
        sortedList() {
            return [...(this.list || [])].sort(
                (a, b) => Number(a.min_spending) - Number(b.min_spending)
            );
        },
        privilegeIcons() {
            return PRIVILEGE_ICONS;
        },
        // Every problem with the list, recomputed as it is typed. The blocking
        // one is checked again in submitForm — this is what paints the warning.
        privilegeErrors() {
            return validatePrivileges(this.form.privileges);
        },
        privilegeIconWarning() {
            return this.privilegeErrors.indexOf(ERR_UNKNOWN_ICON) !== -1;
        },
        // "1 added · 2 changed · 1 removed", or "" when this save would change
        // nothing about the list. A replace-the-whole-list PUT cannot be asked
        // afterwards what it did, so it is stated before the operator commits.
        privilegeDiffText() {
            const diff = privilegesDiff(this.originalPrivileges, this.form.privileges);
            const total =
                diff.added.length + diff.updated.length + diff.removed.length;
            if (!total) return "";
            return this.$t("member_level.privilege_diff", {
                added: diff.added.length,
                updated: diff.updated.length,
                removed: diff.removed.length
            });
        }
    },
    mounted() {
        this.loadList();
    },
    methods: {
        ...mapActions({
            fetchList: "MemberLevel/fetchList",
            createItem: "MemberLevel/create",
            updateItem: "MemberLevel/update",
            removeItem: "MemberLevel/remove"
        }),
        loadList() {
            this.fetchList({});
        },
        money(val) {
            const n = Number(val || 0);
            return "฿" + n.toLocaleString();
        },
        percent(val) {
            const n = Number(val || 0);
            return (n % 1 === 0 ? n : n.toFixed(2)) + "%";
        },
        isDuplicateMin(value) {
            const min = Number(value);
            return (this.list || []).some(
                (t) => Number(t.min_spending) === min && t.id !== this.form.id
            );
        },
        iconClass(name) {
            return privilegeIconClass(name);
        },
        // Count of privileges on a LIST row. An un-migrated database answers no
        // `privileges` at all (§2 guard), which is 0, not an error.
        privilegeCount(row) {
            return Array.isArray(row && row.privileges) ? row.privileges.length : 0;
        },
        activePrivilegeCount(row) {
            if (!Array.isArray(row && row.privileges)) return 0;
            return row.privileges.filter((p) => p && p.is_active).length;
        },
        addPrivilege() {
            this.form.privileges.push(
                withKeys([
                    { icon: "", label_th: "", label_en: "", is_active: true }
                ])[0]
            );
        },
        removePrivilege(index) {
            this.form.privileges.splice(index, 1);
        },
        // ▲/▼ instead of a drag library: array order is sort_order (§5), and a
        // 3-5 row list does not justify a dependency. splice, not index
        // assignment, so Vue 2 sees the change.
        movePrivilege(index, delta) {
            const target = index + delta;
            if (target < 0 || target >= this.form.privileges.length) return;
            const rows = this.form.privileges.slice();
            const moved = rows.splice(index, 1)[0];
            rows.splice(target, 0, moved);
            this.form.privileges = rows;
        },
        openCreate() {
            this.editMode = false;
            this.originalMin = -1;
            this.originalPrivileges = [];
            this.form = blankForm();
            this.dialogVisible = true;
        },
        openEdit(row) {
            this.editMode = true;
            this.originalMin = Number(row.min_spending) || 0;
            // Ordered by the server's sort_order before the editor re-indexes,
            // so opening and saving without touching anything is a no-op.
            this.originalPrivileges = privilegesFromApi(row.privileges);
            this.form = Object.assign(blankForm(), {
                id: row.id,
                code: row.code || "",
                name_th: row.name_th || "",
                name_en: row.name_en || "",
                min_spending: Number(row.min_spending) || 0,
                points_per_baht_x100: Number(row.points_per_baht_x100) || 1,
                free_shipping: !!row.free_shipping,
                discount_percent: Number(row.discount_percent) || 0,
                cashback_percent: Number(row.cashback_percent) || 0,
                color: row.color || "#409EFF",
                icon: row.icon || "",
                benefits_json: row.benefits_json ? JSON.stringify(row.benefits_json) : "",
                privileges: withKeys(this.originalPrivileges)
            });
            this.dialogVisible = true;
        },
        resetForm() {
            this.form = blankForm();
            this.editMode = false;
            this.originalMin = -1;
            this.originalPrivileges = [];
            if (this.$refs.levelForm) {
                this.$refs.levelForm.clearValidate();
            }
        },
        // The two coded 400s of §4 get a sentence of their own; everything else
        // stays with the shared axios-error reader. Both the {code:...} body and
        // a go-core {Msg:"bad_privilege"} envelope are read, because the handler
        // is being written in parallel with this screen.
        levelErrorMessage(err) {
            const data = (err && err.response && err.response.data) || {};
            const code = String(
                data.code || data.Code || data.error_code || data.Msg || ""
            ).trim();
            if (code === "bad_cashback_percent") {
                return this.$t("member_level.err_cashback_percent");
            }
            if (code === "bad_privilege") {
                return this.$t("member_level.err_privilege_label");
            }
            return apiErrorMessage(err, this.$t);
        },
        submitForm() {
            this.$refs.levelForm.validate(async (valid) => {
                if (!valid) return;
                // Both of §4's coded 400s are decidable here, so no request is
                // spent on them — and, more importantly, the operator is told
                // which field is wrong instead of reading a server code.
                const coinError = cashbackPercentError(this.form.cashback_percent);
                if (coinError) {
                    this.$notify({
                        type: "danger",
                        message: this.$t("member_level.err_cashback_" + coinError)
                    });
                    return;
                }
                if (this.privilegeErrors.indexOf(ERR_EMPTY_LABEL_TH) !== -1) {
                    this.$notify({
                        type: "danger",
                        message: this.$t("member_level.err_privilege_label")
                    });
                    return;
                }
                this.saving = true;
                try {
                    let parsedBenefits = null;
                    if (this.form.benefits_json && this.form.benefits_json.trim() !== "") {
                        parsedBenefits = JSON.parse(this.form.benefits_json);
                    }
                    const payload = {
                        code: this.form.code,
                        name_th: this.form.name_th,
                        name_en: this.form.name_en,
                        min_spending: Number(this.form.min_spending),
                        points_per_baht_x100: Number(this.form.points_per_baht_x100),
                        free_shipping: !!this.form.free_shipping,
                        discount_percent: Number(this.form.discount_percent),
                        color: this.form.color,
                        icon: this.form.icon,
                        benefits_json: parsedBenefits,
                        // ALWAYS the full list: §4 reads an omitted `privileges`
                        // as "unchanged", so sending only the rows that changed
                        // would leave the deleted ones alive.
                        privileges: normalizePrivileges(this.form.privileges)
                    };
                    // A cleared coin field means "leave it alone" (§4), which is
                    // spelled by NOT sending the key — 0 would be a deliberate
                    // "this tier has no coin rate of its own".
                    const coin = this.form.cashback_percent;
                    if (coin !== null && coin !== undefined && coin !== "") {
                        payload.cashback_percent = Number(coin);
                    }
                    if (this.editMode) {
                        await this.updateItem({ id: this.form.id, payload });
                    } else {
                        await this.createItem(payload);
                    }
                    this.dialogVisible = false;
                    this.loadList();
                    this.$notify({ type: "success", message: this.$t("member_level.save_success") });
                } catch (err) {
                    this.$notify({ type: "danger", message: this.levelErrorMessage(err) });
                } finally {
                    this.saving = false;
                }
            });
        },
        confirmDelete(row) {
            if (Number(row.min_spending) === 0) {
                this.$notify({ type: "danger", message: this.$t("member_level.err_base_protected") });
                return;
            }
            swal.fire({
                title: this.$t("member_level.delete_title"),
                text: this.$t("member_level.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("member_level.delete_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
/* One privilege = one line. flex rather than an el-table because every cell is
   an input: a table would fight the form's label-width and clip the selects. */
.privilege-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
}
.privilege-row__move {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
/* The ▲/▼/✕ buttons sit inside a form row, so they must not inherit
   base-button's block spacing or the line grows to twice its height. */
.privilege-row__btn {
    margin: 0;
    padding: 2px 6px;
    line-height: 1.2;
}
.privilege-row__icon {
    width: 150px;
    flex: 0 0 150px;
}
/* Fixed width so the label inputs stay aligned whether the preview glyph is a
   wide icon (fa-birthday-cake) or the neutral placeholder. */
.privilege-row__preview {
    width: 18px;
    text-align: center;
    flex: 0 0 18px;
}
.privilege-row__label {
    flex: 1 1 auto;
    min-width: 120px;
}
</style>
